<template>
  <v-combobox
    :model-value="display"
    :items="items"
    item-title="title"
    item-value="value"
    :label="label || undefined"
    :placeholder="placeholder"
    variant="outlined"
    hide-details
    return-object
    clearable
    @update:model-value="onChange"
    @update:search="onSearch"
  />
</template>

<script>
import { mapState } from "vuex";

// Select an existing client or type a new name to create one. The model value is
// the client_id; the human-readable client_name is exposed via update:clientName
// so callers can keep their denormalized client_name field in sync.
export default {
  name: "ClientPicker",
  props: {
    modelValue: { type: String, default: null }, // client_id
    clientName: { type: String, default: null },
    label: { type: String, default: "" },
    placeholder: { type: String, default: "Select or create a client" },
  },
  emits: ["update:modelValue", "update:clientName"],
  computed: {
    ...mapState({ clients: (s) => s.clients }),
    items() {
      return this.clients.map((c) => ({ title: c.name, value: c.uid }));
    },
    display() {
      if (this.modelValue) {
        const c = this.clients.find((x) => x.uid === this.modelValue);
        if (c) return { title: c.name, value: c.uid };
      }
      return this.clientName || null;
    },
  },
  methods: {
    // Fired when the model value commits (item selected or input cleared). This
    // never creates a client — creation is deferred to the caller on an explicit
    // submit so typing can't spawn a client per keystroke.
    onChange(val) {
      // Existing client selected (object from the items list).
      if (val && typeof val === "object" && val.value) {
        this.$emit("update:modelValue", val.value);
        this.$emit("update:clientName", val.title);
        return;
      }
      // Cleared.
      if (!val || (typeof val === "string" && !val.trim())) {
        this.$emit("update:modelValue", null);
        this.$emit("update:clientName", null);
      }
      // Free-text strings are handled by onSearch, not here.
    },
    // Fired on every keystroke. Report the typed name (and match an existing
    // client if the text matches one exactly) without writing anything.
    onSearch(text) {
      const name = String(text || "").trim();
      if (!name) {
        this.$emit("update:clientName", null);
        return;
      }
      const existing = this.clients.find(
        (c) => (c.name || "").toLowerCase() === name.toLowerCase()
      );
      if (existing) {
        this.$emit("update:modelValue", existing.uid);
        this.$emit("update:clientName", existing.name);
      } else {
        // New name — surface it but leave modelValue empty; the caller
        // resolves-or-creates the client when the form is submitted.
        this.$emit("update:modelValue", null);
        this.$emit("update:clientName", name);
      }
    },
  },
};
</script>
